import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box, Typography } from '@mui/material';

ChartJS.register(...registerables);

/**
 * MostUsedMachinesChart - Displays machine usage as bar chart
 * 
 * @param {Array} data - Array of machine data with count value (based on selected date range)
 * @param {string} selectedPeriod - Period label for display (optional, for backward compatibility)
 * @param {boolean} showAllPeriods - If true and data has weekly/monthly/yearly, shows grouped bars
 */
const MostUsedMachinesChart = ({ data = [], selectedPeriod = 'weekly', showAllPeriods = false }) => {
    const barChartRef = useRef(null);
    const barChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { machine_name: '3D Printer', count: 0 },
        { machine_name: 'CNC Machine', count: 0 },
        { machine_name: 'Oscilloscope', count: 0 },
        { machine_name: 'Laser Cutter', count: 0 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.machine_name || item.name);

    // Check if data has period-based values (for backward compatibility)
    const hasPeriodData = chartData.length > 0 && (
        chartData[0].weekly !== undefined ||
        chartData[0].monthly !== undefined ||
        chartData[0].yearly !== undefined
    );

    // Check if data has simple count value (new format)
    const hasCountData = chartData.length > 0 && chartData[0].count !== undefined;

    // Extract values based on data format
    let chartValues;
    if (hasCountData) {
        // New format: single count based on date range
        chartValues = chartData.map(item => item.count || 0);
    } else if (hasPeriodData) {
        // Old format: period-based values
        if (showAllPeriods) {
            // Will be handled separately for grouped bars
            chartValues = chartData.map(item => item[selectedPeriod] || 0);
        } else {
            chartValues = chartData.map(item => item[selectedPeriod] || 0);
        }
    } else {
        // Fallback
        chartValues = chartData.map(item => item.usage_hours || item.hours || 0);
    }

    // Calculate max value
    const maxVal = Math.max(...chartValues, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    // Color definitions for all periods
    const periodColors = {
        weekly: {
            bg: 'rgba(5, 118, 211, 0.15)',
            border: '#0576d3'
        },
        monthly: {
            bg: 'rgba(215, 145, 255, 0.25)',
            border: '#c144ff'
        },
        yearly: {
            bg: 'rgba(255, 210, 102, 0.4)',
            border: '#d6a210'
        }
    };

    useEffect(() => {
        if (barChartRef.current) {
            if (barChartInstance.current) {
                barChartInstance.current.destroy();
            }

            const ctx = barChartRef.current.getContext('2d');


            // Build datasets based on data format
            let datasets;
            if (hasCountData) {
                // New format: single bar per machine showing count in selected range
                datasets = [
                    {
                        label: 'Reservations',
                        data: chartValues,
                        backgroundColor: 'rgba(5, 118, 211, 0.25)',
                        borderColor: '#0576d3',
                        borderWidth: 1,
                        barPercentage: 0.6,
                        categoryPercentage: 0.8
                    }
                ];
            } else if (showAllPeriods && hasPeriodData) {
                // Old format with grouped bars (backward compatibility)
                const weeklyValues = chartData.map(item => item.weekly || 0);
                const monthlyValues = chartData.map(item => item.monthly || 0);
                const yearlyValues = chartData.map(item => item.yearly || 0);
                datasets = [
                    {
                        label: 'Weekly',
                        data: weeklyValues,
                        backgroundColor: periodColors.weekly.bg,
                        borderColor: periodColors.weekly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    },
                    {
                        label: 'Monthly',
                        data: monthlyValues,
                        backgroundColor: periodColors.monthly.bg,
                        borderColor: periodColors.monthly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    },
                    {
                        label: 'Yearly',
                        data: yearlyValues,
                        backgroundColor: periodColors.yearly.bg,
                        borderColor: periodColors.yearly.border,
                        borderWidth: 1,
                        barPercentage: 0.7,
                        categoryPercentage: 0.85
                    }
                ];
            } else {
                const colors = periodColors[selectedPeriod] || periodColors.weekly;
                datasets = [
                    {
                        label: 'Reservations',
                        data: chartValues,
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        borderWidth: 1,
                        barPercentage: 0.6,
                        categoryPercentage: 0.8
                    }
                ];
            }

            barChartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false }, // We use custom legend
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            callbacks: {
                                label: (context) => `${context.dataset.label}: ${context.raw} reservations`
                            }
                        },
                        datalabels: { display: false }
                    },
                    scales: {
                        x: {
                            display: true,
                            grid: { display: false },
                            ticks: {
                                color: '#666666',
                                font: { size: 10, family: 'Inter, sans-serif' },
                                maxRotation: 45,
                                minRotation: 45
                            },
                            border: { display: true, color: '#333333' }
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                precision: 0,
                                color: '#666666',
                                font: { size: 10, family: 'Inter, sans-serif' }
                            },
                            grid: { color: '#f0f0f0', drawBorder: false },
                            border: { display: false }
                        }
                    }
                }
            });
        }
        return () => {
            if (barChartInstance.current) {
                barChartInstance.current.destroy();
            }
        };
    }, [data, selectedPeriod, showAllPeriods, labels, suggestedMax, chartValues, hasPeriodData, hasCountData, chartData]);

    const hasData = chartData.some(item => {
        if (hasCountData) {
            return (item.count || 0) > 0;
        }
        if (hasPeriodData) {
            return (item.weekly || 0) > 0 || (item.monthly || 0) > 0 || (item.yearly || 0) > 0;
        }
        return (item.usage_hours || item.hours || 0) > 0;
    });

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', gap: { xs: '4px', sm: '10px' } }}>
            <Box sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                color: '#333',
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' }
            }}>
                No. of Users
            </Box>
            <Box className="group" sx={{ flex: 1, height: '100%', minHeight: { xs: '280px', sm: '320px' }, position: 'relative' }}>
                {!hasData && data.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography sx={{ color: '#999', fontSize: '14px' }}>
                            No machine usage data available
                        </Typography>
                    </Box>
                ) : (
                    <canvas ref={barChartRef} style={{ width: '100%', height: '100%' }}></canvas>
                )}
            </Box>
        </Box>
    );
};

export default MostUsedMachinesChart;
